import { useEffect, useId, useState } from 'react';
import type { CustomCategory } from '@shared/schemas';
import { LIMITS } from '@shared/constants';
import { useT } from '../i18n';
import { CATEGORY_COLORS } from '../lib/emojis';
import { newId } from '../lib/ids';
import { Dialog } from './Dialog';
import { EmojiPicker } from './EmojiPicker';
import { Notice } from './Notice';

interface Props {
  open: boolean;
  category: CustomCategory | null;
  /** Names already in use (built-in and custom), lower-cased. */
  takenNames: string[];
  onClose(): void;
  onSave(category: CustomCategory): void;
}

export function CategoryEditor({ open, category, takenNames, onClose, onSave }: Props) {
  const t = useT();
  const nameId = useId();
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('⭐');
  const [color, setColor] = useState(CATEGORY_COLORS[5]!);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(category?.name ?? '');
    setIcon(category?.icon ?? '⭐');
    setColor(category?.color ?? CATEGORY_COLORS[5]!);
    setError(null);
  }, [open, category]);

  function submit() {
    const trimmed = name.trim();
    if (!trimmed) return setError(t('categoryNameRequired'));
    const taken = takenNames.includes(trimmed.toLowerCase()) && trimmed.toLowerCase() !== category?.name.toLowerCase();
    if (taken) return setError(t('categoryNameTaken'));
    onSave({ id: category?.id ?? newId('cat'), name: trimmed, icon, color });
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={category ? t('editCategoryTitle') : t('newCategoryTitle')}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('cancel')}
          </button>
          <button type="button" className="btn btn--primary" onClick={submit}>
            {t('save')}
          </button>
        </>
      }
    >
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <div className="category-preview" style={{ borderColor: color }}>
          <span aria-hidden="true">{icon}</span> {name.trim() || '…'}
        </div>
        <div className="field">
          <label htmlFor={nameId}>{t('categoryName')}</label>
          <input id={nameId} value={name} maxLength={LIMITS.maxCategoryNameLength} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <span className="field__label">{t('categoryColor')}</span>
          <div className="color-grid" role="radiogroup" aria-label={t('categoryColor')}>
            {CATEGORY_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={color === c}
                aria-label={c}
                className="color-option"
                style={{ background: c }}
                onClick={() => setColor(c)}
              />
            ))}
          </div>
        </div>
        <div className="field">
          <span className="field__label">{t('categoryIcon')}</span>
          <EmojiPicker value={icon} onChange={setIcon} label={t('categoryIcon')} />
        </div>
        {error && <Notice kind="error">{error}</Notice>}
      </form>
    </Dialog>
  );
}
